import React, { useEffect } from 'react';
import SEO from '../SEO';
import seoData from '../../seoData';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { CheckCircle, ArrowRight, Shield, TrendingUp, Clock } from 'lucide-react';
import Header from '../Header';
import Footer from '../Footer';
import PageHero from '../ui/PageHero';
import { useNavigate } from 'react-router-dom';

const AssetLifeExtension = () => {
  const navigate = useNavigate();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <SEO title={seoData["services/asset-life-extension"]?.title} description={seoData["services/asset-life-extension"]?.description} path="/services/asset-life-extension" keywords={seoData["services/asset-life-extension"]?.keywords || []} />

      <PageHero
        badge="Core Services"
        title="Asset Life Extension & Restoration"
        description="Extend the service life of industrial assets with protective coatings, corrosion repair, and infrastructure rehabilitation. Industrial asset restoration contractor serving California. Reduce capital expenditure with in-situ repair solutions that prevent tank failure and stop corrosion."
        backgroundKey="asset-life-extension"
        buttonText="Get In Touch"
      />

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Problems We Solve</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {['Aging infrastructure approaching end of rated service life',
              'Capital budget constraints forcing deferred maintenance',
              'Escalating repair costs from corrosion and deterioration',
              'Unplanned failures causing production losses and safety risks',
              'Regulatory pressure to maintain and protect aging assets',
              'Rising replacement costs for tanks, pipes, and equipment'].map((challenge, index) => (
              <div key={index} className="flex items-start p-4 bg-emerald-50 rounded-lg">
                <TrendingUp className="text-emerald-600 mr-3 mt-0.5 flex-shrink-0" size={20} />
                <span className="text-gray-700 text-sm">{challenge}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-slate-50">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-bold text-slate-900 mb-8 text-center">Our Solutions</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                        <Card className="border-l-4 border-l-emerald-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Clock className="text-emerald-600 mr-3 flex-shrink-0" size={22} />
                  Proactive Corrosion Assessment
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Comprehensive corrosion assessment and life extension planning for aging assets. Identify corrosion rates, predict remaining life, and develop cost-effective coating and repair strategies before failure occurs.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-emerald-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Clock className="text-emerald-600 mr-3 flex-shrink-0" size={22} />
                  Protective Coating Application
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Apply protective coating systems matched to your service environment. Extend steel, concrete, and equipment life with corrosion-resistant coatings that prevent further deterioration and extend time between maintenance cycles.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-emerald-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Clock className="text-emerald-600 mr-3 flex-shrink-0" size={22} />
                  In-Situ Equipment Rebuild
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Repair and restore aging equipment in place using epoxy composite systems. Pumps, valves, heat exchangers, and rotating equipment returned to OEM specifications without replacement.</p>
              </CardContent>
            </Card>
            <Card className="border-l-4 border-l-emerald-600">
              <CardHeader>
                <CardTitle className="flex items-center text-lg">
                  <Clock className="text-emerald-600 mr-3 flex-shrink-0" size={22} />
                  Infrastructure Rehabilitation Programs
                </CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-gray-600 text-sm leading-relaxed">Phased rehabilitation programs for tanks, pipes, structures, and facilities. Prioritize repairs by criticality and condition to maximize asset life while managing budget constraints.</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
            <div>
              <h2 className="text-3xl font-bold text-slate-900 mb-6">Why Choose BCS</h2>
              <div className="space-y-3">
                {['Extend asset service life 15-25 years with protective coatings',
                  'Reduce CAPEX — repair costs 50-70% less than replacement',
                  'Prevent unplanned failures with proactive assessment and repair',
                  'Prioritized rehabilitation — address critical assets first',
                  'Minimize production downtime with in-situ repair methods',
                  'Full documentation for asset management and regulatory compliance',
                  'Manufacturer-backed warranties on all repair and coating systems',
                  'Single contractor for assessment, repair, coating, and QA/QC'].map((benefit, index) => (
                  <div key={index} className="flex items-start">
                    <CheckCircle className="text-green-700 mr-3 mt-0.5 flex-shrink-0" size={18} />
                    <span className="text-gray-700 text-sm">{benefit}</span>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-blue-900 text-white p-8 rounded-lg">
              <Shield className="mb-4" size={48} />
              <h3 className="text-xl font-bold mb-4">Belzona Products We Use</h3>
              <div className="space-y-3">
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 1111 (Super Metal)</p>
                  <p className="text-xs text-slate-500 mt-1">General purpose equipment rebuild</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 5811 (Immersion Grade)</p>
                  <p className="text-xs text-slate-500 mt-1">Tank and pipe interior coating</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 5831 (ST-Barrier)</p>
                  <p className="text-xs text-slate-500 mt-1">Atmospheric corrosion protection</p>
                </div>
                <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
                  <p className="font-semibold text-sm text-slate-900">Belzona 4111 (Magma-Quartz)</p>
                  <p className="text-xs text-slate-500 mt-1">Concrete infrastructure repair</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-blue-900 text-white">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-3xl font-bold mb-4">Don't Replace It — Extend Its Life.</h2>
          <p className="text-blue-100 mb-8">Contact us for a free asset assessment and life extension strategy. We'll show you how to save 50-70% vs. replacement while extending service life by decades.</p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Button size="lg" className="bg-white text-blue-900 hover:bg-gray-100" onClick={() => window.location.href = 'tel:+19493719948'}>
              Call (949) 371-9948
            </Button>
            <Button size="lg" variant="outline" className="border-white text-white hover:bg-blue-800" onClick={() => navigate('/#contact')}>
              Request Assessment <ArrowRight className="ml-2" size={20} />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default AssetLifeExtension;
